const DB_NAME = "thryiv-kiosk";
const DB_VERSION = 1;

const STORES = {
  PHOTOS: "photos",
  PUNCH_QUEUE: "punch_queue",
};

let dbPromise = null;

function openDb() {
  if (dbPromise) {
    return dbPromise;
  }

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      if (!db.objectStoreNames.contains(STORES.PHOTOS)) {
        const photoStore = db.createObjectStore(STORES.PHOTOS, {
          keyPath: "id",
          autoIncrement: true,
        });
        photoStore.createIndex("employeeId", "employeeId", { unique: false });
        photoStore.createIndex("timestamp", "timestamp", { unique: false });
        photoStore.createIndex("synced", "synced", { unique: false });
      }

      if (!db.objectStoreNames.contains(STORES.PUNCH_QUEUE)) {
        const queueStore = db.createObjectStore(STORES.PUNCH_QUEUE, {
          keyPath: "id",
          autoIncrement: true,
        });
        queueStore.createIndex("createdAt", "createdAt", { unique: false });
        queueStore.createIndex("synced", "synced", { unique: false });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

  return dbPromise;
}

function runTransaction(storeName, mode, callback) {
  return openDb().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, mode);
        const store = tx.objectStore(storeName);
        const result = callback(store, tx);

        tx.oncomplete = () => resolve(result);
        tx.onerror = () => reject(tx.error);
      })
  );
}

export async function savePhoto({
  employeeId,
  type,
  timestamp,
  blob,
  synced = false,
}) {
  const record = {
    employeeId,
    type,
    timestamp,
    blob,
    synced,
    createdAt: Date.now(),
  };

  return runTransaction(STORES.PHOTOS, "readwrite", (store) => {
    store.add(record);
  });
}

export async function getPhotos() {
  return runTransaction(STORES.PHOTOS, "readonly", (store) =>
    new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    })
  );
}

export async function clearPhotos() {
  return runTransaction(STORES.PHOTOS, "readwrite", (store) => {
    store.clear();
  });
}

export async function pruneOldPhotos(retentionDays = 30) {
  const cutoff = Date.now() - retentionDays * 24 * 60 * 60 * 1000;
  const photos = await getPhotos();
  const stale = photos.filter((p) => p.createdAt < cutoff);

  if (stale.length === 0) {
    return 0;
  }

  await runTransaction(STORES.PHOTOS, "readwrite", (store) => {
    stale.forEach((photo) => store.delete(photo.id));
  });

  return stale.length;
}

export async function enqueuePunch({ employeeId, action, timestamp }) {
  const record = {
    employeeId,
    action,
    timestamp,
    synced: false,
    createdAt: Date.now(),
    retryCount: 0,
  };

  return runTransaction(STORES.PUNCH_QUEUE, "readwrite", (store) => {
    store.add(record);
  });
}

export async function getPendingPunches() {
  return runTransaction(STORES.PUNCH_QUEUE, "readonly", (store) =>
    new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => {
        const all = request.result || [];
        resolve(all.filter((item) => !item.synced));
      };
      request.onerror = () => reject(request.error);
    })
  );
}

export async function markPunchSynced(id) {
  return runTransaction(STORES.PUNCH_QUEUE, "readwrite", (store) =>
    new Promise((resolve, reject) => {
      const getReq = store.get(id);
      getReq.onsuccess = () => {
        const record = getReq.result;
        if (!record) {
          resolve(false);
          return;
        }
        record.synced = true;
        store.put(record);
        resolve(true);
      };
      getReq.onerror = () => reject(getReq.error);
    })
  );
}

export async function removeSyncedPunches() {
  const all = await runTransaction(STORES.PUNCH_QUEUE, "readonly", (store) =>
    new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    })
  );

  const synced = all.filter((item) => item.synced);
  if (synced.length === 0) {
    return 0;
  }

  await runTransaction(STORES.PUNCH_QUEUE, "readwrite", (store) => {
    synced.forEach((item) => store.delete(item.id));
  });

  return synced.length;
}

export async function getQueueStats() {
  const pending = await getPendingPunches();
  const photos = await getPhotos();
  return {
    pendingCount: pending.length,
    photoCount: photos.length,
  };
}
